import {
  abs,
  clamp,
  cos,
  dot,
  exp,
  float,
  floor,
  fract,
  length,
  max,
  mix,
  mod,
  sin,
  smoothstep,
  step,
  vec2,
} from 'three/tsl';

import { solarRamp } from '@/app/lcv/components/sections/spartan/engine/sections/hero-animation/webgpu/backdrop/colorRamp';
import {
  groundProjection,
  type GroundProjectionParams,
} from '@/app/lcv/components/sections/spartan/engine/sections/hero-animation/webgpu/backdrop/dot-grid-ground/groundProjection';
import {
  NEAR_ZERO_3,
  NEAR_ZERO_4,
} from '@/app/lcv/components/sections/spartan/shared/webgpu/constants';

// 2D value noise and fbm for the ground grid.
//
// It lives in its own module because it was once shared with a ring of
// fire on the same plane, where two copies of a hash would have drifted
// and read as unrelated patterns crossing rather than as one surface
// doing two things. The fire is gone -- the bump-down splash is the
// PowerRing now -- but a hash worth not duplicating is worth keeping
// where a second caller can find it.
//
// The hash is the one from the Shadertoy flame this was ported from. The
// cos-inside-sin is not a tidier hash written badly; it is what gives this
// particular noise its slightly clumpy, uneven character, which is most of
// why the fire looks like fire. Swapping it for a cleaner hash changes the
// look.

const hash2 = (n: any): any =>
  fract(sin(cos(dot(n, vec2(12.9898, 12.1414)))).mul(83758.5453));

// Hash the four lattice corners and blend. smoothstep on the cell fraction
// rather than a linear blend is what stops the underlying grid showing
// through as diamonds.
const valueNoise2d = (n: any): any => {
  const base: any = floor(n);
  const f: any = smoothstep(vec2(0, 0), vec2(1, 1), fract(n));

  return mix(
    mix(hash2(base), hash2(base.add(vec2(1, 0))), f.x),
    mix(hash2(base.add(vec2(0, 1))), hash2(base.add(vec2(1, 1))), f.x),
    f.y
  );
};

// Unrolled rather than looped: the octave count is a compile-time number,
// and an unrolled chain avoids the var/Loop dance TSL needs to carry an
// accumulator across iterations.
//
// `coord += coord * 1.7` is lacunarity 2.7, which is unusually wide. The
// octaves land far apart, so the result is chunky cells with fine detail
// rather than the even mush a conventional 2.0 progression gives.
const fbm2d = (n: any, octaves = 5): any => {
  let total: any = float(0);
  let coord: any = n;
  let amplitude = 1;

  for (let octave = 0; octave < octaves; octave += 1) {
    total = total.add(valueNoise2d(coord).mul(amplitude));
    coord = coord.add(coord.mul(1.7));
    amplitude *= 0.47;
  }

  return total;
};

// What fbm2d sums to at full amplitude, so a caller can divide back to a
// roughly 0..1 range. Worth having as a function rather than a magic
// number per call site: change the octave count and the normalisation
// follows, instead of quietly rescaling everything downstream.
const fbm2dRange = (octaves = 5): number => {
  let total = 0;
  let amplitude = 1;

  for (let octave = 0; octave < octaves; octave += 1) {
    total += amplitude;
    amplitude *= 0.47;
  }

  return total;
};

// The ground: a dot lattice on a virtual floor, fading out toward the
// horizon.
//
// WHY A PATTERN RATHER THAN INSTANCED DOTS. The obvious reading of "each
// dot rides the wave" is one mesh per dot, which at a useful grid density
// is thousands of objects to build, place and animate. Drawing the dots as
// a PATTERN instead means the wave can carry them by displacing the
// coordinate they are drawn in -- the same read, for one draw call and no
// per-dot state.
//
// IT USED TO CARRY THEM BY DISPLACING GEOMETRY, which cost a subdivided
// sheet and bounded the wave's resolution to the vertex grid: a wave
// narrower than the segment spacing aliased. Everything is per-pixel now,
// so that constraint is gone and the quad is two triangles.
//
// THE SYNC. Everything is driven off one `elapsed` uniform, so the reveal
// sweep and the wave cannot drift apart.

export interface DotGridRippleParams {
  // Seconds since impact. Held at a negative value before the landing, so
  // every term below folds to nothing and the ground is flat and quiet.
  elapsed: any;
  // Where the engine struck, in the plane's own local XY. Still needed
  // once the shockwave has gone: the reveal front and its turn-on glow
  // both spread from it.
  impact: any;

  // -- THE BANG. A global brightness pop across every dot at the instant
  // of impact, decaying away. Distinct from the ring's own lift glow,
  // which is local and travels: this one lands everywhere at once, so the
  // two read as separate layers -- a flash, and then a wave moving through
  // what the flash lit.
  flashStrength: any;
  flashDecay: any;
  // SECONDS THE FLASH WAITS after the clock starts.
  //
  // The clock now starts BEFORE contact, so that the wave has time to
  // spread ahead of the falling engine. The flash is the detonation
  // though, and a detonation before the thing lands reads as a mistake --
  // so it alone is held back by exactly the lead the clock was given, and
  // still fires on the frame of impact.
  flashDelay: any;

  // -- THE IDLE. A wave of light travelling ACROSS the grid, left to
  // right, fading in after the impact and then running forever so the
  // ground is never completely dead once it has been struck.
  //
  // IT MOVES THE FLOOR COORDINATE, not the geometry. See `idleHeight`.
  //
  // SINE PLUS NOISE, and the noise does two separate jobs.
  //
  // A pure sine travelling along X is a straight vertical bar sliding
  // sideways -- unmistakably a formula, and the eye locks onto its period
  // within one cycle. The fix is not "more noise" but noise in the right
  // two places:
  //
  //   `idleWarp`     bends the PHASE, so the wavefront is a wandering line
  //                  rather than a ruler. This is the one that matters --
  //                  it destroys the readable period while keeping the
  //                  left-to-right travel completely intact.
  //   `idleNoiseMix` modulates the AMPLITUDE, so the wave is strong in
  //                  some places and barely there in others, and no two
  //                  passes look the same.
  //
  // Both at zero gives the pure sine back, which is worth keeping as a
  // reference point when tuning.
  idleGlow: any;
  idleFrequency: any;
  idleSpeed: any;
  idleNoiseScale: any;
  idleNoiseSpeed: any;
  idleWarp: any;
  idleNoiseMix: any;
  // HOW FAR THE WAVE MOVES THE DOTS, peak to trough, in FLOOR-COORDINATE
  // units -- the same space `panX`/`panY` are in.
  //
  // This is what puts the lattice itself into the wave rather than only
  // lighting it. It displaced vertices once; on a camera-facing quad that
  // means nothing, so it displaces the coordinate the lattice is drawn in
  // instead. In a raked view "up off the floor" and "further along it"
  // land in nearly the same screen direction, so the read is the same.
  // 0 keeps the lattice still and leaves the wave as light alone.
  idleHeight: any;
  // HOW MUCH THE WAVE'S TROUGH FADES DOTS OUT -- the half of the wave
  // that sits BELOW the plane's resting level, where the sheet has dipped
  // under the floor line.
  //
  // Reads as depth rather than as light: dots sinking away and coming back
  // as the wave passes, which gives the grid a thickness it does not have
  // when every dot is equally present. Pairs with `idleFade` on the other
  // half -- crest and trough can be faded by different amounts, or either
  // one alone.
  idleTroughFade: any;
  // HOW MUCH THE WAVE'S CREST FADES DOTS OUT. The crest is where the field
  // is densest, and fading there rather than brightening makes the wave
  // read as passing THROUGH the grid -- dots dropping out and coming back
  // as it goes over them, instead of a highlight sliding across a surface
  // that never changes.
  //
  // Independent of `idleGlow` on purpose: a crest can brighten, or fade,
  // or do both at different strengths.
  idleFade: any;
  // How long the idle takes to emerge. Long enough that it is not
  // competing with the bang for attention.
  idleFadeInSeconds: any;

  // -- THE REVEAL. The grid does not exist before the landing; the impact
  // switches it on, outward from the point of contact, like a wave of
  // lights coming up.
  //
  // Distinct from the flash, which brightens dots that are ALREADY there.
  // This one decides whether a dot is there at all, and it travels.
  revealSpeed: any;
  // How soft the on/off boundary is, in local units. A hard edge reads as
  // an expanding disc of paint; a soft one reads as lights coming on.
  revealSoftness: any;
  // How much brighter a dot is at the instant it lights, and how quickly
  // it settles to the grid's resting brightness. This is the "very bright"
  // part -- the front is a bright band, not a uniform switch.
  turnOnStrength: any;
  turnOnDecay: any;
}

export interface DotGridLookParams {
  // Dots across the plane. A count, not a spacing, so the pattern stays
  // put when the plane is resized.
  gridCount: any;
  dotRadius: any;
  dotSoftness: any;
  // Every Nth cell on BOTH axes is drawn as a cross instead of a dot, so
  // the field reads as a measured grid rather than as wallpaper -- the
  // marks give the eye something to register the ripple's travel against.
  //
  // The test is on the cell INDEX, not on position, which is why it stays
  // put when the plane is resized: `fract` gives where you are inside a
  // cell, `floor` gives which cell it is.
  markEvery: any;
  // Arm half-length and half-thickness, in cell units. The dot's own
  // radius is the useful reference: arms noticeably longer than it are
  // what make a mark read as a mark rather than as a fatter dot.
  markArmLength: any;
  markArmThickness: any;
  // How much tighter the grid gets toward the rim. 0 is a uniform grid;
  // the density ratio between rim and centre is (1+k)/(1-k), so 0.5 is
  // three times tighter at the edge. See the warp for why it is this
  // curve and not a power.
  gridCompression: any;
  // HOW MUCH BRIGHTER A MARK IS THAN A DOT.
  //
  // The marks are the grid's ruler -- they exist so the eye has fixed
  // reference points to measure the ripple's travel against. At the same
  // brightness as the dots they stop doing that job: the field flattens
  // into one texture and reads as wallpaper. Above 1 they hold the lattice
  // together.
  //
  // They are also held OUT of the idle's fade, which is what makes
  // "always brighter" true rather than merely usually true -- a reference
  // that disappears when the wave passes over it is not a reference.
  markBoost: any;
  // Where the grid starts and finishes dissolving, as a fraction of the
  // plane's half-size. This is the "depth" fade: without it the grid ends
  // at a hard square edge, which reads as a floating card rather than as
  // ground running away from you.
  fadeStart: any;
  fadeEnd: any;
  brightness: any;
  // The ceiling on a dot's intensity.
  //
  // THIS IS WHAT CAPS THE BRIGHTNESS. It used to be a hard 1, which is the
  // top of the colour ramp -- so past that point raising `brightness` did
  // nothing at all, because there was nowhere left to go.
  //
  // The material is additive, so anything above 1 simply adds more light
  // than the ramp's top stop: the dot blows out. That is what a bloom pass
  // would give you for the cost of a full-screen blur, except here it is
  // one number and it is free. A real bloom would spread the light into
  // neighbouring pixels, which this does not -- if you want the halo
  // rather than the punch, that is when a post pass earns its cost.
  dotCeiling: any;
  colorLow: any;
  colorMid: any;
  colorHigh: any;
  opacity: any;
}

// THE SHOCKWAVE RING IS GONE FROM THIS PLANE.
//
// It used to be a Gaussian band travelling out from the impact, lifting
// the sheet in the vertex stage and drawn again as a continuous band of
// light in the fragment stage. The bump-down splash is now the PowerRing
// plane lying on this ground instead, so the ring lived here only to
// compete with it: two rings leaving the same point at two speeds, one of
// them dragging a 200x200 segment sheet along with it.
//
// What that leaves behind on purpose: the FLASH (a global pop across
// every dot at the instant of contact), the REVEAL (the front that
// switches the grid on) and the IDLE. None of those is a ring, and all
// three still read as the floor responding to the landing.
//
// The fire ring went with it, for the same reason -- see
// createDotGridPositionNode.

// 1 where the reveal has already passed, 0 ahead of it. Everything the
// grid draws is gated on this, so before the landing there is genuinely
// nothing on screen rather than something drawn at zero opacity.
const revealMask = (ripple: DotGridRippleParams, coord: any) => {
  const { elapsed, impact, revealSpeed, revealSoftness } = ripple;

  const distance: any = length(coord.sub(impact));
  const front: any = elapsed.mul(revealSpeed);

  // Edges reversed, so the result is 1 BEHIND the front rather than ahead
  // of it. Before impact `elapsed` is negative, the front sits behind every
  // real distance, and the whole grid is off -- no branch needed.
  return smoothstep(
    front,
    front.sub(max(revealSoftness, float(NEAR_ZERO_3))),
    distance
  );
};

// How recently this dot lit up: 1 at the instant the front reaches it,
// decaying after. This is the bright leading band.
const turnOnGlow = (ripple: DotGridRippleParams, coord: any) => {
  const { elapsed, impact, revealSpeed, turnOnDecay } = ripple;

  const distance: any = length(coord.sub(impact));
  // Seconds since the front passed THIS dot. Negative ahead of it, which
  // exp() would turn into a huge number, so it is clamped at 0.
  const sinceLit: any = max(
    elapsed.sub(distance.div(max(revealSpeed, float(NEAR_ZERO_3)))),
    0
  );

  return exp(sinceLit.mul(turnOnDecay).negate());
};

// The idle wave, 0..1, before glow and fade are applied. Returns the raw
// shape so the caller can drive BOTH brightness and opacity from the same
// number -- two effects on one wave, rather than two waves that have to be
// kept in step.
const idlePulse = (ripple: DotGridRippleParams, coord: any) => {
  const {
    elapsed,
    idleFrequency,
    idleSpeed,
    idleNoiseScale,
    idleNoiseSpeed,
    idleWarp,
    idleNoiseMix,
  } = ripple;

  // The noise field, drifting. Sampled in FLOOR space so the pattern is
  // fixed to the ground -- sampling in screen or uv space would make it
  // swim as the projection is re-aimed, which instantly reads as a texture
  // pinned over the view rather than as something lying on the floor.
  //
  // This used to read `positionLocal.xy`, which was the same thing back
  // when the quad was really raked. On a camera-facing quad those are
  // screen coordinates, so left alone the wave would cross the screen at a
  // constant rate with no foreshortening at all.
  //
  // Three octaves, not five: this is a soft modulation, and the finest two
  // octaves of a 2.7-lacunarity fbm land well below the dot spacing, so
  // they would cost twelve more hashes per pixel to produce detail that
  // falls between the dots and is never sampled.
  const field: any = fbm2d(
    coord.mul(idleNoiseScale).add(vec2(elapsed.mul(idleNoiseSpeed), 0)),
    3
  ).div(fbm2dRange(3));

  // TRAVELLING ALONG FLOOR X, left to right. The phase is warped by the
  // field, which is what turns the wavefront from a straight bar into a
  // wandering edge without touching its direction or speed.
  const phase: any = coord.x
    .mul(idleFrequency)
    .sub(elapsed.mul(idleSpeed))
    .add(field.sub(0.5).mul(idleWarp));

  // Remapped to 0..1 rather than left as the raw -1..1 sine. As light the
  // trough of a signed wave would subtract brightness and punch dark bands
  // into the resting grid.
  const wave: any = sin(phase).mul(0.5).add(0.5);

  // Amplitude modulated by the same field. `idleNoiseMix` is how far the
  // weakest parts drop: 0 leaves every crest equal, 1 lets the field
  // silence the wave entirely where it is dark.
  const shaped: any = wave.mul(
    mix(float(1).sub(clamp(idleNoiseMix, 0, 1)), float(1), field)
  );

  // Eased in from the impact, so it emerges out of the bang instead of
  // switching on. Held at zero beforehand by the same negative `elapsed`
  // that gates the ring.
  const gate: any = smoothstep(
    float(0),
    max(ripple.idleFadeInSeconds, float(NEAR_ZERO_3)),
    elapsed
  );

  return shaped.mul(gate);
};

// `enableIdle` is a BUILD-TIME switch, not a uniform. Setting the idle's
// strength to zero leaves its three-octave noise in the compiled shader,
// still evaluated per vertex and per pixel to produce a value that is then
// multiplied away; passing false removes it from the graph entirely.
//
// That distinction is the whole point of having it: it is the only way to
// find out what the idle actually costs, since every other dial can hide
// the effect without removing the work.
// REMOVED, and worth saying why rather than leaving a gap.
//
// This used to return a `positionNode` that lifted the sheet's vertices
// along their normal, so the dots physically rode the idle wave. That only
// means anything on a plane the camera sees at an angle. The quad is now
// camera-facing and screen-sized, so its normal points straight at the
// lens: displacing along it moves vertices toward and away from the camera,
// which produces a sub-pixel scale change and nothing else -- while still
// paying for three octaves of noise on every one of the sheet's vertices.
//
// The wave still moves the dots; it does so by displacing the FLOOR
// COORDINATE instead, which is the same picture in a projection where
// "up off the floor" is a direction in the image rather than in the mesh.
// See `idleRide` in the colour node.
//
// It also means the quad no longer needs to be subdivided at all: two
// triangles carry the whole effect, because every part of it is now
// per-pixel.
export const createDotGridColorNode = (
  ripple: DotGridRippleParams,
  look: DotGridLookParams,
  // WHERE THE FLOOR IS, as seen from here. See groundProjection: this is
  // what replaced raking the quad itself, and every coordinate below comes
  // out of it rather than out of `uv()` or `positionLocal`.
  projection: GroundProjectionParams,
  // Build-time -- see the note above on `enableIdle`.
  enableIdle = true,
  // A screen-space crop (see home/engine/webgpu/frame-mask), 1 inside the
  // frame and 0 outside it. Multiplied into COLOUR AND OPACITY, the same
  // way powerRingShader applies its own.
  //
  // Opacity alone is not enough here: this plane blends additively, so the
  // colour channel is added to the framebuffer and unmasked dots keep
  // drawing straight across the crop. An earlier version masked only the
  // alpha, on the theory that doing both would dim the surviving pixels,
  // and the floor ran outside the card while the ring stayed inside it.
  //
  // The doubling that theory worried about is real but confined to the
  // feather: the mask is 1 inside the frame and 0 outside, and 1 and 0 are
  // both unchanged by squaring. Only the 2px soft edge lands slightly
  // harder, which is well below what the feather exists to hide.
  //
  // THIS DOES NOT SKIP THE FBM ABOVE. A screen-space test cannot run
  // before the fragment's world position (and therefore this plane's
  // fbm) has already been evaluated -- multiplying it in here only
  // changes what reaches the framebuffer, not what the shader computed to
  // get there. Omitted entirely by every LCV call site, so nothing here
  // changes for that page.
  mask?: any
) => {
  const {
    gridCount,
    dotRadius,
    dotSoftness,
    markEvery,
    markArmLength,
    markArmThickness,
    markBoost,
    gridCompression,
    fadeStart,
    fadeEnd,
    brightness,
    dotCeiling,
    colorLow,
    colorMid,
    colorHigh,
    opacity,
  } = look;

  // THE GRID'S SPACING, TIGHTENING TOWARD THE EDGES.
  //
  // WHY THIS IS NOT A CAMERA PROBLEM. Perspective already compresses the
  // grid, but it compresses with distance from the CAMERA -- on a raked
  // plane that means the far edge, not the rim. Radial tightening measured
  // from the plane's own centre is a different thing entirely, and no
  // field of view produces it. Widening the FOV to chase it would also
  // rescale everything else in the shot.
  //
  // The warp is `v * mix(1, |v|, k)` on each axis, over -1..1:
  //
  //   * SEPARABLE, so rows stay rows and columns stay columns. A radial
  //     warp would bend the lattice into rings and the "+" marks would
  //     stop reading as a lattice at all.
  //   * ENDPOINT-PRESERVING -- the rim maps to the rim, so tightening
  //     redistributes the dots instead of shrinking the field.
  //   * NON-DEGENERATE AT THE CENTRE. The obvious choice, pow(|v|, n), has
  //     zero slope at 0, which blows the middle cell up to swallow the
  //     area right under the engine -- a bald patch exactly where the eye
  //     is. This one's slope is (1-k) at the centre and (1+k) at the rim,
  //     so the density ratio is a finite (1+k)/(1-k) and the centre stays
  //     a grid.
  //
  // At k = 0 it is the identity, so 0 is exactly the uniform grid.
  //
  // APPLIED INSIDE THE PROJECTION, before the rake -- see
  // groundProjection's own note on the ordering.
  const floorSpace = groundProjection(projection, gridCompression);

  // THE WAVE, computed from the UNRIDDEN coordinate.
  //
  // It has to be sampled before the ride below displaces anything, or the
  // wave would be feeding back into its own input and the crests would
  // chase themselves across the floor.
  const idle: any = enableIdle ? idlePulse(ripple, floorSpace.coord) : float(0);

  // THE DOTS RIDING THE WAVE.
  //
  // This is what the vertex displacement used to do -- see the note where
  // that was removed. Pushing the floor coordinate along its own Y is the
  // projected image of lifting the sheet: in a raked view "up off the
  // floor" and "further along the floor" land in nearly the same screen
  // direction, so displacing the coordinate reads as the surface
  // undulating rather than as the texture sliding over it.
  //
  // Centred on the midpoint for the same reason the displacement was:
  // `idle` is the 0..1 wave, so 0.5 is the resting height, and subtracting
  // it is what gives the wave a trough instead of only ever pushing.
  const ridden: any = vec2(
    floorSpace.coord.x,
    floorSpace.coord.y.add(idle.sub(0.5).mul(ripple.idleHeight))
  );

  // Remapped back to the 0..1 the cell arithmetic below expects. The floor
  // coordinate is unbounded -- that is the whole point of it near the
  // horizon -- so this is a rescale, not a clamp: `fract` below is what
  // makes the lattice repeat across it.
  const warped: any = ridden.mul(0.5).add(0.5);

  // Which cell, and where inside it. floor() gives the index the mark test
  // needs; fract() gives the local coordinate the shapes are drawn in.
  //
  // Note the shapes are still drawn in CELL space, so a dot in a tight
  // cell is drawn smaller in world space -- the whole pattern scales down
  // rather than the dots crowding at a fixed size, which is what keeps it
  // reading as the same grid seen tighter.
  const scaled: any = warped.mul(gridCount);
  const index: any = floor(scaled);
  const cell: any = fract(scaled).sub(0.5);

  const dot: any = float(1).sub(
    smoothstep(
      max(dotRadius.sub(dotSoftness), float(0)),
      max(dotRadius, float(NEAR_ZERO_3)),
      length(cell)
    )
  );

  // THE MARKS. mod(index, N) is 0 on every Nth cell; step(.., 0.5) turns
  // that into a 1/0 without a branch, since the values are whole numbers
  // and nothing else lands below 0.5. Multiplying the two axes means only
  // the INTERSECTIONS are marked -- both axes at once, so the marks sit on
  // a lattice rather than in stripes.
  const period: any = max(markEvery, float(1));
  const isMark: any = step(mod(index.x, period), 0.5).mul(
    step(mod(index.y, period), 0.5)
  );

  // A cross is two bars, each clipped to its own arm length. max() rather
  // than add(): where they overlap at the centre, adding would double the
  // value and the middle would read as a bright knot.
  const soft: any = max(dotSoftness, float(NEAR_ZERO_3));
  const bar = (across: any, along: any) =>
    float(1)
      .sub(
        smoothstep(markArmThickness.sub(soft), markArmThickness, abs(across))
      )
      .mul(
        float(1).sub(
          smoothstep(markArmLength.sub(soft), markArmLength, abs(along))
        )
      );

  const plus: any = max(bar(cell.y, cell.x), bar(cell.x, cell.y));

  // The marked cells swap shape entirely rather than adding a cross on top
  // of a dot -- overlaid, the dot fills the middle and the result is a
  // blob with spikes.
  //
  // The cross carries `markBoost`, so a mark is brighter than a dot before
  // anything else in this shader touches it. Pushed into the shape rather
  // than added later so it rides the depth fade and the reveal like every
  // other part of the grid -- a mark that stayed bright as the floor faded
  // into the distance would read as a sprite pinned over the scene.
  const mark: any = mix(dot, plus.mul(max(markBoost, float(0))), isMark);

  // THE DEPTH FADE, now measured along the floor rather than radially from
  // the plane's centre.
  //
  // Radial was right when the quad was the floor: the grid running away
  // from the middle of the sheet was the thing receding. Projected, the
  // direction that recedes is toward the HORIZON, and `distance` is 0 at
  // the near edge and 1 there -- so the fade dissolves the far ground,
  // which is both what it looks like and what stops the lattice aliasing
  // where an unbounded number of cells fall into one pixel.
  //
  // At pitch 0 `distance` collapses back to the old radial measure, so the
  // un-raked look is unchanged.
  const depthFade: any = float(1).sub(
    smoothstep(
      fadeStart,
      max(fadeEnd, fadeStart.add(float(NEAR_ZERO_3))),
      floorSpace.distance
    )
  );

  // TWO LAYERS OF BRIGHTNESS now that the ring has gone:
  //
  //   flash -- every dot at once, decaying. The detonation.
  //   idle  -- the travelling wave that outlives it, driving opacity as
  //            well as brightness.
  //
  // The third used to be `lift`: the dots the shockwave ring was
  // currently raising, brightened as it passed. The PowerRing plane draws
  // that pass now, so the dots no longer need to carry it.
  //
  // `idle` itself is computed much further up, because the lattice needs
  // it to ride on -- see `ridden`. One evaluation feeds both the ride and
  // the light, which is also what keeps them in step: two calls would be
  // two samples of the same wave and could disagree by a frame.

  // THE WAVE'S CREST FADING DOTS OUT, and the marks refusing to.
  //
  // `1 - isMark` is the whole mechanism: on a marked cell the fade term is
  // multiplied by zero, so the cross keeps its full strength while the
  // dots around it drop away. That is what makes the lattice hold together
  // as the wave crosses -- the marks stay put and the field breathes
  // between them.
  // BOTH HALVES MEASURED FROM THE MIDPOINT, not from zero. `idle` is the
  // 0..1 wave, so 0.5 is the plane's own resting height -- exactly where
  // the displacement crosses zero and the sheet is neither raised nor
  // sunk. Splitting there is what lets the two dials mean "fade the part
  // above the floor" and "fade the part below it" independently.
  //
  // Measuring the crest from 0 instead, as this did at first, left every
  // dot on the plane half-faded at rest and made the two dials impossible
  // to reason about separately.
  const crestHeight: any = clamp(idle.sub(0.5).mul(2), 0, 1);
  const troughDepth: any = clamp(float(0.5).sub(idle).mul(2), 0, 1);

  // The crest fade spares the marks -- see markBoost. The TROUGH fade does
  // not: it is the surface having dropped below the floor, which happens
  // to a mark exactly as much as to a dot. `markBoost` is a multiplier, so
  // the marks stay proportionally brighter as both sink together, and
  // "always brighter than the dot" survives.
  const idleFadeAmount: any = crestHeight
    .mul(ripple.idleFade)
    .mul(float(1).sub(isMark))
    .add(troughDepth.mul(ripple.idleTroughFade));
  const idleOpacity: any = clamp(float(1).sub(idleFadeAmount), 0, 1);

  // Measured from the moment of contact rather than from the clock's
  // start -- see flashDelay.
  const sinceFlash: any = ripple.elapsed.sub(ripple.flashDelay);
  const flash: any = ripple.flashStrength
    .mul(exp(max(sinceFlash, 0).mul(ripple.flashDecay).negate()))
    .mul(smoothstep(float(-0.001), float(0), sinceFlash));

  // FOUR LAYERS NOW. The reveal GATES everything -- a dot the front has
  // not reached yet contributes nothing at all -- and the turn-on glow
  // rides the front itself, so the leading edge is a bright band that
  // settles to the resting grid behind it.
  const revealed: any = revealMask(ripple, floorSpace.coord);
  const turnOn: any = turnOnGlow(ripple, floorSpace.coord).mul(
    ripple.turnOnStrength
  );

  const intensity: any = clamp(
    mark
      .mul(depthFade)
      .mul(brightness)
      .mul(revealed)
      .mul(idleOpacity)
      .mul(
        float(1)
          .add(turnOn)
          .add(flash)
          // Added directly, no longer scaled by `liftGlow`. That dial
          // exists to say how much brighter a LIFTED dot gets, and the
          // idle no longer lifts anything -- borrowing it meant the two
          // could not be tuned apart. `idleGlow` is the whole answer for
          // how strong the wave's light is.
          //
          // Scaled by idleGlow, NOT added raw. Added raw it reached +1 on
          // the crest -- nearly doubling brightness exactly where the
          // crest fade was removing the dot, so the two cancelled and the
          // wave was invisible no matter how either dial was set.
          .add(idle.mul(ripple.idleGlow))
      ),
    0,
    dotCeiling
  );

  // ONE EMISSION. There used to be a second one summed in here -- the
  // shockwave drawn as a continuous band of light with its own palette,
  // sitting outside the dot mask so it read as a solid ring rather than a
  // row of dots blinking in sequence.
  //
  // That band is what the PowerRing plane now is, and it draws it far
  // better: a real field with filaments and a trail, on one quad, instead
  // of a Gaussian profile evaluated per pixel across this entire plane.
  const emission: any = solarRamp(intensity, colorLow, colorMid, colorHigh).mul(
    intensity
  );

  // THE ALPHA HAS TO CARRY THE INTENSITY, not sit at a flat 1.
  //
  // A constant alpha makes the plane a fully opaque surface everywhere,
  // including the vast majority of it where nothing is lit -- so the
  // geometry reads as a black card rather than as a grid floating in
  // space. It only looked right under additive blending, where a black
  // source adds nothing; under any other mode, or against anything the
  // plane should be showing through to, it is a solid rectangle.
  //
  // Dividing the summed emission back out by the total recovers the
  // colour, so `colour x alpha` still equals the emission exactly. The
  // difference is that alpha is now genuinely 0 where there is no light,
  // and the plane is transparent there for real.
  // THE RING OF FIRE, added as its own emission rather than routed through
  // a ramp. It arrives already coloured and already tone-mapped -- the
  // whole point of its Reinhard step is that the ramp comes out of the
  // exposure -- so pushing it through solarRamp as well would flatten
  // exactly the gradient it exists to produce.
  //
  // The dots are the only thing this plane emits now -- see the note on
  // `emission` for what the second and third layers were.
  const total: any = intensity;
  const colour: any = emission.div(max(total, float(NEAR_ZERO_4)));

  const totalOpacity: any = clamp(total, 0, 1);

  // THE CROP GOES INTO BOTH OUTPUTS, not just the alpha.
  //
  // This plane blends ADDITIVELY, and additive blending adds the colour
  // channel to whatever is already in the framebuffer. Masking only the
  // alpha therefore leaves the dots drawing right across the crop -- the
  // floor ran outside the card while the power ring, which folds its mask
  // into both, stayed inside it.
  //
  // Same shape as powerRingShader's `alpha`: one term, multiplied into
  // the colour and the opacity together, so the two cannot disagree about
  // where the frame ends.
  // THE HORIZON GOES INTO BOTH OUTPUTS, for exactly the reason the crop
  // does: additive blending adds the colour channel regardless of alpha,
  // so masking only the opacity would leave the lattice drawing across the
  // sky half of the screen.
  const grounded: any = colour.mul(floorSpace.mask);
  const groundedOpacity: any = totalOpacity.mul(floorSpace.mask);

  const cropped: any = mask ? grounded.mul(mask) : grounded;

  // `opacity` SCALES THE COLOUR, and the alpha carries the dots' own
  // intensity. Additive blending means the contribution is rgb x a, so
  // which of the two the factor rides makes no difference to what is
  // drawn.
  //
  // It was briefly moved onto the alpha instead, to stop a hidden ground
  // (opacity 0) emitting (0, 0, 0, dots) -- black dots with the lattice
  // still in the alpha channel, which is how the canvas composites over
  // the page. That was a real property of this output, but it was NOT
  // what was wrong: the ground was hidden because the page had stopped
  // ever raising its visibility, and the move broke the look, so it is
  // reverted here.
  //
  // If a genuinely transparent hidden state is wanted later, gate the
  // DRAW rather than the colour -- DotGridGround already switches
  // `material.visible` off below the same threshold.
  return {
    colorNode: cropped.mul(opacity),
    opacityNode: mask ? groundedOpacity.mul(mask) : groundedOpacity,
  };
};
